import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { useInvestmentsVsDebt } from '../../hooks/useInvestmentsVsDebt';
import { ValueStepper } from '../../modules/Onboarding/components/ValueStepper';
import {
  DEFAULT_ANNUAL_RATE_PCT,
  MAX_ANNUAL_RATE_PCT,
  MIN_ANNUAL_RATE_PCT,
} from '../../utils/investmentCompounding';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { formatCurrency } from './d3/format';
import { DualLineChart } from './DualLineChart';
import { GlassButton } from '../ui/GlassButton';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';

type Holding = { value: number; expectedReturnPct?: number };
type SeriesPoint = { year: number; balance: number };

type Props = {
  holdings: Holding[];
  mortgageBalanceSeries: SeriesPoint[];
  startYear?: number;
  /** Compact Home widget copy. */
  compact?: boolean;
  headerAction?: { label: string; onPress: () => void };
};

/**
 * Investments vs debt with visible, editable assumed return %.
 */
export function InvestmentsVsDebtCard({
  holdings,
  mortgageBalanceSeries,
  startYear,
  compact = false,
  headerAction,
}: Props) {
  const investmentHoldings = useMemo(
    () => holdings.filter((h) => h.value > 0),
    [holdings],
  );

  const model = useInvestmentsVsDebt({
    holdings: investmentHoldings,
    mortgageBalanceSeries,
    startYear,
  });

  const rateEdited =
    Math.abs(model.annualRatePct - model.seedReturnPct) > 0.05;

  return (
    <GlassCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>Investments vs debt</Text>
          <Text style={styles.hint}>
            {compact
              ? 'Projected path until investments overtake your home loan'
              : 'Home loan liability vs investments'}
            {model.crossoverYear
              ? ` · crossover ~${model.crossoverYear}`
              : ''}
          </Text>
        </View>
        <View style={styles.headerActions}>
          <RefreshButton compact />
          {headerAction ? (
            <GlassButton
              variant="ghost"
              size="sm"
              onPress={headerAction.onPress}
              accessibilityLabel={headerAction.label}
            >
              {headerAction.label}
            </GlassButton>
          ) : null}
        </View>
      </View>

      <View style={styles.rateBlock}>
        <ValueStepper
          label="Assumed investment return"
          value={model.annualRatePct}
          suffix="%"
          decimals
          min={MIN_ANNUAL_RATE_PCT}
          max={MAX_ANNUAL_RATE_PCT}
          hint={
            rateEdited
              ? `Default ${DEFAULT_ANNUAL_RATE_PCT}% · portfolio avg ${model.seedReturnPct}%`
              : `Default ${DEFAULT_ANNUAL_RATE_PCT}%`
          }
          onChange={model.setAnnualRatePct}
          onDecrease={() => model.nudgeAnnualRatePct(-0.5)}
          onIncrease={() => model.nudgeAnnualRatePct(0.5)}
        />
        {rateEdited ? (
          <GlassButton
            variant="ghost"
            size="sm"
            onPress={model.resetToPortfolio}
            accessibilityLabel="Reset return to portfolio average"
          >
            Reset to portfolio
          </GlassButton>
        ) : null}
        <Text style={styles.principalHint}>
          Starting investments {formatCurrency(model.investmentPrincipal)} ·{' '}
          {model.annualRatePct}% / yr
        </Text>
      </View>

      <DualLineChart
        seriesA={model.mortgageBalanceSeries}
        seriesB={model.investmentBalanceSeries}
        labelA="Home loan"
        labelB="Investments"
        crossoverYear={model.crossoverYear || undefined}
      />
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  rateBlock: {
    gap: spacing.xs,
  },
  principalHint: {
    ...typography.caption,
    color: colors.textMuted,
  },
});
